import { Link } from "react-router-dom";
import {
    Table,
    TableHeader,
    TableColumn,
    TableBody,
    TableRow,
    TableCell,
    Card,
    CardHeader,
    CardBody,
    CardFooter,
    Button,
    Tooltip,
    Chip,
} from "@heroui/react";
import { LuPlus, LuPencil, LuTrash } from "react-icons/lu";
import { useDataTable, useFetchData } from "@/hooks";
import {
    DataTableControlBar,
    DataTableSkeleton,
    DataTablePagination,
    FilterButton,
    DeleteConfirmModal,
    ActiveFilterChips,
} from "@/components/data-table";
import {
    EmptyState,
    ErrorState,
    PageHeader,
    ThemedText,
} from "@/components/ui";
import { FilterData } from "./components";
import { useUser } from "@/context/UserContext";

const initialFilters = {
    name: "",
    transaction_category_type_id: "",
};

const TransactionCategoryData = () => {
    const { hasPermission } = useUser();
    const hasAnyAction = hasPermission("transaction-category.edit") || hasPermission("transaction-category.delete");
    const table = useDataTable(
        "transaction-category/data",
        "transaction-category/{id}/delete",
        {},
        initialFilters,
    );

    const { data: types } = useFetchData("select/transaction-category-type");

    const filterLabels = {
        name: { label: "Nama" },
        transaction_category_type_id: {
            label: "Tipe",
            getValueLabel: (val) => {
                const item = types?.find(
                    (t) => String(t.value) === String(val),
                );
                return item?.title || val;
            },
        },
    };

    const handleRemoveFilter = (key) => {
        table.filterPanel.setPending((prev) => ({
            ...prev,
            [key]: initialFilters[key],
        }));
        table.filters.setFilter(key, initialFilters[key]);
    };

    const skeletonColumns = [
        { name: "No.", skeletonWidth: "w-8" },
        { name: "Nama", skeletonWidth: "w-40" },
        { name: "Tipe", skeletonWidth: "w-32" },
        { name: "Akun Jurnal", skeletonWidth: "w-48" },
        { name: "Keterangan", skeletonWidth: "w-48" },
        ...(hasAnyAction ? [{ name: "Aksi", skeletonWidth: "w-20", align: "end" }] : []),
    ];

    const columns = [
        { key: "no", label: "No." },
        { key: "name", label: "Nama" },
        { key: "type", label: "Tipe" },
        { key: "journal_account", label: "Akun Jurnal" },
        { key: "description", label: "Keterangan" },
        ...(hasAnyAction ? [{ key: "actions", label: "Aksi", align: "end" }] : []),
    ];

    const renderCell = (item, columnKey) => {
        switch (columnKey) {
            case "no":
                return (
                    <ThemedText size="sm" variant="secondary">
                        {(table.pagination.page - 1) *
                            table.pagination.perPage +
                            table.items.indexOf(item) +
                            1}
                        .
                    </ThemedText>
                );
            case "name":
                return <ThemedText size="sm">{item.name}</ThemedText>;
            case "type":
                return item.type ? (
                    <Chip
                        color={item.type.badge_color}
                        size="sm"
                        variant="flat"
                    >
                        {item.type.name}
                    </Chip>
                ) : (
                    "-"
                );
            case "journal_account":
                return (
                    <ThemedText size="sm">
                        {item.journal_account_id
                            ? item.journal_account
                                ? `${item.journal_account.account_code} - ${item.journal_account.name}`
                                : item.journal_account_id
                            : "-"}
                    </ThemedText>
                );
            case "description":
                return (
                    <ThemedText
                        size="xs"
                        variant="caption"
                        className="line-clamp-1"
                    >
                        {item.description || "-"}
                    </ThemedText>
                );
            case "actions":
                return (
                    <div className="flex justify-end gap-2">
                        {hasPermission("transaction-category.edit") && (
                            <Tooltip content="Edit">
                                <Button
                                    isIconOnly
                                    size="sm"
                                    variant="light"
                                    as={Link}
                                    to={`/transaction-category/${item.id}/edit`}
                                >
                                    <LuPencil
                                        size={14}
                                        className="text-default-500"
                                    />
                                </Button>
                            </Tooltip>
                        )}
                        {hasPermission("transaction-category.delete") && (
                            <Tooltip content="Hapus">
                                <Button
                                    isIconOnly
                                    size="sm"
                                    variant="light"
                                    color="danger"
                                    onPress={() =>
                                        table.deleteItem(item.id)
                                    }
                                >
                                    <LuTrash size={14} />
                                </Button>
                            </Tooltip>
                        )}
                    </div>
                );
            default:
                return null;
        }
    };

    if (table.error) {
        return (
            <div className="p-6">
                <ErrorState
                    title="Gagal Memuat Data"
                    description="Terjadi kesalahan saat memuat data Kategori Transaksi."
                    onAction={table.refetch}
                />
            </div>
        );
    }

    return (
        <div className="flex h-full w-full flex-col gap-4">
            <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
                <PageHeader
                    title="Kategori Transaksi"
                    breadcrumbs={[
                        { label: "Dashboard", href: "/" },
                        { label: "Keuangan", href: "/finance" },
                        { label: "Kategori Transaksi" },
                    ]}
                />
                <div className="flex items-center gap-2">
                    <FilterButton
                        isOpen={table.filterPanel.isOpen}
                        onToggle={() =>
                            table.filterPanel.setIsOpen(
                                !table.filterPanel.isOpen,
                            )
                        }
                        activeCount={table.filters.activeCount}
                    />
                    {hasPermission("transaction-category.create") && (
                        <Button
                            as={Link}
                            to="/transaction-category/create"
                            color="primary"
                            startContent={<LuPlus />}
                            size="sm"
                        >
                            Tambah Data
                        </Button>
                    )}
                </div>
            </div>

            {/* Filter Panel */}
            <FilterData
                table={table}
                initialFilters={initialFilters}
                filterOptions={{ types: types || [] }}
            />

            <ActiveFilterChips
                filters={table.filters.values}
                filterLabels={filterLabels}
                onRemove={handleRemoveFilter}
            />

            <DeleteConfirmModal table={table} />

            <Card className="border-default-200 border" shadow="none">
                <CardHeader>
                    <DataTableControlBar table={table} />
                </CardHeader>
                <CardBody className="p-0">
                    {table.isLoading ? (
                        <DataTableSkeleton
                            columns={skeletonColumns}
                            rowCount={table.pagination.perPage}
                        />
                    ) : table.items.length === 0 ? (
                        <EmptyState
                            title={
                                table.filters.activeCount > 0
                                    ? "Data Tidak Ditemukan"
                                    : "Belum Ada Data"
                            }
                            description={
                                table.filters.activeCount > 0
                                    ? "Tidak ada data yang cocok dengan filter."
                                    : "Belum ada data Kategori Transaksi."
                            }
                        />
                    ) : (
                        <Table
                            aria-label="Transaction Category Table"
                            shadow="none"
                        >
                            <TableHeader columns={columns}>
                                {(column) => (
                                    <TableColumn
                                        key={column.key}
                                        align={column.align}
                                    >
                                        {column.label}
                                    </TableColumn>
                                )}
                            </TableHeader>
                            <TableBody items={table.items}>
                                {(item) => (
                                    <TableRow key={item.id}>
                                        {(columnKey) => (
                                            <TableCell>
                                                {renderCell(item, columnKey)}
                                            </TableCell>
                                        )}
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    )}
                </CardBody>
                <CardFooter>
                    <DataTablePagination table={table} />
                </CardFooter>
            </Card>
        </div>
    );
};

export default TransactionCategoryData;
